<template>
  <div class="chat-composer">
    <!-- Linked page: what the item will point back to, and a way to drop it. -->
    <div class="chat-composer__link-row">
      <template v-if="context">
        <span class="chat-composer__link">
          <span class="chat-composer__link-icon" v-html="linkIcon" />
          <span class="chat-composer__link-label">{{ context.label }}</span>
          <button
            type="button"
            class="chat-composer__link-remove"
            title="Don't link this page"
            @click="dismissed = true"
          >
            <span v-html="crossIcon" />
          </button>
        </span>
        <span class="chat-composer__link-note">Auto-linked</span>
      </template>
      <template v-else-if="detected">
        <button type="button" class="chat-composer__link chat-composer__link--empty" @click="dismissed = false">
          <span class="chat-composer__link-icon" v-html="linkIcon" />
          <span class="chat-composer__link-label">Link {{ detected.label }}</span>
        </button>
        <span class="chat-composer__link-note">Not linked</span>
      </template>
    </div>

    <div
      ref="inputWrapRef"
      class="chat-composer__input-wrap"
      :class="{ 'chat-composer__input-wrap--filled': canSubmit }"
    >
      <textarea-custom
        :key="composerKey"
        ref="textareaRef"
        v-model="body"
        class="chat-composer__textarea"
        placeholder="Message your team — @mention to assign"
        :autosize="true"
        :min-height="62"
        :max-height="130"
        :max-symbols="false"
        @keydown="onKeydown"
      />

      <div class="chat-composer__actions">
        <button type="button" class="chat-composer__icon-btn" title="Mention someone" @click="insertMention">
          <span class="chat-composer__btn-icon chat-composer__btn-icon--at">@</span>
        </button>

        <button
          v-if="!deadline"
          ref="deadlineButtonRef"
          type="button"
          class="chat-composer__icon-btn"
          title="Add deadline"
          @click="openDeadlinePicker"
        >
          <span class="chat-composer__btn-icon" v-html="calendarIcon" />
        </button>
        <span v-else ref="deadlineButtonRef" class="chat-composer__deadline-chip">
          Due {{ deadlineLabel }}
          <button type="button" class="chat-composer__chip-remove" title="Remove deadline" @click="deadline = null">
            <span v-html="crossIcon" />
          </button>
        </span>

        <!-- Tagged recipients — a deadline or a tag both mean "task". -->
        <span v-if="taggedRecipients.length" class="chat-composer__recipients">
          <user-avatar
            :name="taggedRecipients[0].name"
            :url="taggedRecipients[0].photo"
            size="x-small"
            class="chat-composer__recipient-avatar"
          />
          <span class="chat-composer__recipient-name">{{
            taggedRecipients.length === 1 ? taggedRecipients[0].name : `${taggedRecipients.length} people`
          }}</span>
          <button type="button" class="chat-composer__chip-remove" title="Clear recipients" @click="clearRecipients">
            <span v-html="crossIcon" />
          </button>
        </span>

        <div class="chat-composer__actions-right">
          <fs-button
            v-if="!deadline"
            :secondary="!sendHighlighted"
            size="small"
            title="Send message (Ctrl/Cmd+Enter)"
            :disabled="!canSubmit"
            @click="submit(ChatItemKind.Message)"
          >
            <template #icon><span class="chat-composer__btn-icon" v-html="sendIcon" /></template>
          </fs-button>
          <fs-button
            :secondary="sendHighlighted"
            size="small"
            title="Assign Task (Enter)"
            :disabled="!canSubmit"
            @click="submit(ChatItemKind.Task)"
          >
            <template #icon><span class="chat-composer__btn-icon" v-html="taskIcon" /></template>
            Assign Task
          </fs-button>
        </div>
      </div>

      <!-- @mention dropdown — anchored at the typed "@" -->
      <fs-popper
        :reference="mentionAnchor"
        :show="mentionOpen"
        :close-on-outside-click="false"
        placement="top-start"
        :offset="[0, 8]"
        animation="none"
        no-styling
      >
        <div class="chat-composer__mentions">
          <div class="chat-composer__mentions-head">Assign to</div>
          <ul class="chat-composer__mentions-list">
            <li
              v-for="(candidate, index) in mentionResults"
              :key="candidate.kind + ':' + (candidate.kind === 'user' ? candidate.user.id : candidate.role.id)"
              class="chat-composer__mention"
              :class="{ 'chat-composer__mention--highlighted': index === highlightedIndex }"
              @mousedown.prevent
              @click="pickCandidate(candidate)"
              @mousemove="highlightedIndex = index"
            >
              <template v-if="candidate.kind === 'user'">
                <user-avatar :name="candidate.user.name" :url="candidate.user.photo" size="x-small" />
                <span class="chat-composer__mention-name">{{ candidate.user.name }}</span>
              </template>
              <template v-else>
                <span class="chat-composer__role-chip">@{{ candidate.role.name }}</span>
                <span class="chat-composer__mention-meta">{{ candidate.members.length }} people</span>
              </template>
            </li>
          </ul>
        </div>
      </fs-popper>
    </div>

    <fs-popper
      :reference="deadlineButtonRef"
      :show="deadlinePickerOpen"
      :close-on-outside-click="true"
      placement="top-start"
      :offset="[0, 4]"
      animation="none"
      no-styling
      @close="deadlinePickerOpen = false"
    >
      <div class="chat-composer__deadline-popper">
        <fs-date-picker inline :model-value="deadline ?? undefined" @pick="onDeadlinePicked" />
      </div>
    </fs-popper>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { ChatItemKind, type UserDto } from '@/ApiClient';
import { useBrandUsers } from '@/store/brand-users';
import { useRoles } from '@/store/roles';
import type { RoleDto } from '@/ApiClient';
import { useChat } from '@/store/chat';
import { useGeneral } from '@/store/general';
import { useChatContext, type ChatContext } from '@lib/chat-context';
import { caretViewportRect } from '@lib/caret-position';
import { removeUserMentions } from '@lib/chat-mentions';
import { dateToDateOnlyString } from '@lib/formatters';
import { format } from 'date-fns';
import UserAvatar from '@components/user/user-avatar.vue';
import FsButton from '@components/buttons/fs-button.vue';
import FsDatePicker from '@components/form-elements/fs-date-picker.vue';
import FsPopper from '@components/poppers/fs-popper.vue';
import TextareaCustom from '@components/form-elements/textarea-custom.vue';
import crossIcon from '@icons/other/ico-cross.svg';
import linkIcon from '@icons/other2/link-chain.svg';
import sendIcon from '@icons/other2/send.svg';
import taskIcon from '@icons/custom-fields/checklist.svg';
import calendarIcon from '@icons/other2/calendar2.svg';

const emit = defineEmits<{
  'recipients-change': [ids: string[]];
  sent: [payload: { kind: ChatItemKind; recipientIds: string[] }];
}>();

const getContext = useChatContext();

const body = ref('');
const taggedRecipients = ref<UserDto[]>([]);
const inputWrapRef = ref<HTMLElement>();
const textareaRef = ref<{ focus: () => void }>();

// What the current page resolves to, and whether the user has waved it off.
// Keeping the detection separate from the dismissal is what lets the dashed
// chip offer it back.
const detected = ref<ChatContext | null>(getContext());
const dismissed = ref(false);
const context = computed<ChatContext | null>(() => (dismissed.value ? null : detected.value));

const deadline = ref<Date | null>(null);
const deadlineButtonRef = ref<HTMLElement>();
const deadlinePickerOpen = ref(false);
const deadlineLabel = computed(() => (deadline.value ? format(deadline.value, 'd MMM') : ''));
const openDeadlinePicker = () => (deadlinePickerOpen.value = true);
const onDeadlinePicked = (value: Date) => {
  deadline.value = value;
  deadlinePickerOpen.value = false;
};

// The composer is rendered behind v-if on the panel — mount = panel-opened.
onMounted(() => textareaRef.value?.focus());

const redetect = () => {
  detected.value = getContext();
  dismissed.value = false;
};

// Navigation is a strong signal of new intent — refresh the linked-page chip on
// route change, matching what we already do after a successful submit. If the
// user dismisses the chip and stays on the same page, the dismissal sticks.
watch(useRoute(), redetect);

// Some labels derive from the tab title (e.g. a measurement chart's name, which
// loads in async). Refresh the chip when it settles so a stale placeholder isn't
// left behind when the panel was already open during navigation.
watch(() => useGeneral().headTitle, redetect);

// Bumped after sending to remount the textarea so its char counter resets.
const composerKey = ref(0);

const focusTextarea = () => nextTick(() => textareaRef.value?.focus());

// The @ button is a shortcut to what typing "@" already does.
const insertMention = () => {
  body.value = body.value.length && !body.value.endsWith(' ') ? `${body.value} @` : `${body.value}@`;
  focusTextarea();
};

// @mention: match a trailing "@query" (no spaces) being typed at the end.
const mentionMatch = computed(() => body.value.match(/@([^\s@]*)$/));
const mentionDismissed = ref(false);
watch(body, () => (mentionDismissed.value = false));

type MentionCandidate = { kind: 'user'; user: UserDto } | { kind: 'role'; role: RoleDto; members: UserDto[] };

const mentionResults = computed<MentionCandidate[]>(() => {
  if (mentionDismissed.value || !mentionMatch.value) return [];

  const query = mentionMatch.value[1].toLowerCase();
  const myId = useChat().meId;
  const taggedIds = new Set(taggedRecipients.value.map((user) => user.id));

  const users = useBrandUsers()
    .loaded.filter((user) => user.id !== myId && !taggedIds.has(user.id) && user.name.toLowerCase().includes(query))
    .slice(0, 6)
    .map((user) => ({ kind: 'user', user }) as MentionCandidate);

  const roles = useRoles()
    .loaded.filter((role) => role.name.toLowerCase().includes(query))
    .slice(0, 3)
    .map((role) => {
      const members = useBrandUsers().loaded.filter((u) => u.roleIds?.includes(role.id));
      return { kind: 'role', role, members } as MentionCandidate;
    });

  return [...roles, ...users];
});

const mentionOpen = computed(() => mentionResults.value.length > 0);

// Virtual popper reference positioned at the "@" character in the textarea.
const mentionAnchor = {
  getBoundingClientRect: (): DOMRect => {
    const textarea = inputWrapRef.value?.querySelector('textarea');
    const at = mentionMatch.value?.index;
    if (!textarea || at == null) return new DOMRect();

    const { top, left, height } = caretViewportRect(textarea, at);
    return new DOMRect(left, top, 0, height);
  },
};

const highlightedIndex = ref(0);
watch(mentionResults, () => (highlightedIndex.value = 0));

const emitRecipients = () =>
  emit(
    'recipients-change',
    taggedRecipients.value.map((user) => user.id)
  );

// Adds a single user to the recipients list (deduped). Does not touch the body.
const pushRecipient = (user: UserDto) => {
  if (taggedRecipients.value.some((u) => u.id === user.id)) return false;
  taggedRecipients.value.push(user);
  return true;
};

const addRecipient = (user: UserDto) => {
  pushRecipient(user);
  // Keep the mention in the body — swap the typed "@query" for the full name.
  body.value = body.value.replace(/@([^\s@]*)$/, `@${user.name} `);
  emitRecipients();
};

const pickCandidate = (candidate: MentionCandidate) => {
  if (candidate.kind === 'user') {
    addRecipient(candidate.user);
    return;
  }
  // Role: expand to all current members as recipients (snapshot); inline keeps @RoleName.
  const myId = useChat().meId;
  for (const member of candidate.members) {
    if (member.id === myId) continue;
    pushRecipient(member);
  }
  body.value = body.value.replace(/@([^\s@]*)$/, `@${candidate.role.name} `);
  emitRecipients();
};

// Drops this user's mentions from the body. The naive `@${name}` string replace
// clipped the front of a longer name that starts the same way — see
// removeUserMentions.
const removeRecipient = (user: UserDto) => {
  body.value = removeUserMentions(
    body.value,
    useBrandUsers().loaded.map((u) => ({ id: u.id, name: u.name })),
    useRoles().loaded.map((role) => ({ id: role.id, name: role.name })),
    user.id
  );

  taggedRecipients.value = taggedRecipients.value.filter((u) => u.id !== user.id);
  emitRecipients();
};

const clearRecipients = () => {
  [...taggedRecipients.value].forEach(removeRecipient);
};

// Tag a user from elsewhere (e.g. clicking a name in a message). Deliberately
// does not emit `recipients-change`, so it never touches the panel's filters.
const tagUser = (userId: string) => {
  if (userId === useChat().meId) return;
  const user = useBrandUsers().getById(userId);
  if (user && !taggedRecipients.value.some((u) => u.id === user.id)) taggedRecipients.value.push(user);
};
defineExpose({ tagUser });

// Holding Ctrl/Cmd previews which button its Enter will press, so the shortcut is
// discoverable rather than folded into a comment. Tracked on the window: the
// modifier can go down or come back up while focus sits anywhere in the panel,
// and a release outside the page only ever reaches us as a blur.
const modifierHeld = ref(false);
const syncModifier = (event: KeyboardEvent) => (modifierHeld.value = event.ctrlKey || event.metaKey);
const clearModifier = () => (modifierHeld.value = false);

onMounted(() => {
  window.addEventListener('keydown', syncModifier);
  window.addEventListener('keyup', syncModifier);
  window.addEventListener('blur', clearModifier);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', syncModifier);
  window.removeEventListener('keyup', syncModifier);
  window.removeEventListener('blur', clearModifier);
});

// Only while the send button is actually on screen. With a deadline the row is
// task-only and submit() forces the task path, so Ctrl/Cmd+Enter assigns as well
// — promoting a hidden button would promise something that will not happen.
const sendHighlighted = computed(() => modifierHeld.value && !deadline.value);

const canSubmit = computed(() => body.value.trim().length > 0);

const submit = async (kind: ChatItemKind) => {
  if (!canSubmit.value) return;
  const recipientIds = taggedRecipients.value.map((user) => user.id);
  // A picked deadline forces the task path regardless of which button was clicked.
  const effectiveKind = deadline.value ? ChatItemKind.Task : kind;
  await useChat().create({
    recipientIds,
    kind: effectiveKind,
    body: body.value.trim(),
    deadline: deadline.value ? dateToDateOnlyString(deadline.value) : undefined,
    contextUrl: context.value?.url,
    contextLabel: context.value?.label,
  });

  body.value = '';
  taggedRecipients.value = [];
  deadline.value = null;
  composerKey.value += 1;
  emit('sent', { kind: effectiveKind, recipientIds });
  redetect();
};

const onKeydown = (event: KeyboardEvent) => {
  // The Enter that commits an IME composition is not a submit, and its arrow
  // keys are not the dropdown's either.
  if (event.isComposing) return;

  // Navigate the @mention dropdown when it is open.
  if (mentionResults.value.length > 0) {
    const count = mentionResults.value.length;

    if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
      event.preventDefault();
      highlightedIndex.value = (highlightedIndex.value + 1) % count;
      return;
    }
    if (event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)) {
      event.preventDefault();
      highlightedIndex.value = (highlightedIndex.value - 1 + count) % count;
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const candidate = mentionResults.value[highlightedIndex.value];
      if (candidate) pickCandidate(candidate);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      mentionDismissed.value = true;
      return;
    }
  }

  if (event.key !== 'Enter') return;

  // Ctrl/Cmd+Enter sends a plain message. It is the secondary action in the
  // button row, and this is its only keyboard route now that Enter assigns.
  // With a deadline set submit() forces the task path anyway, which matches the
  // row: the message button is hidden in that state.
  if (event.ctrlKey || event.metaKey) {
    event.preventDefault();
    submit(ChatItemKind.Message);
    return;
  }

  // Enter assigns a task, following the primary "Assign Task" button rather than
  // the muted send icon beside it. Shift+Enter keeps the newline.
  if (!event.shiftKey) {
    event.preventDefault();
    submit(ChatItemKind.Task);
  }
};
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.chat-composer {
  padding: 1.4rem 2.4rem 2rem;
  background: $c-bg-gray-darker-hover;
  border-top: 1px solid rgba($c-ink, 0.07);

  // ── Linked page ──────────────────────────────────────────────────────────
  &__link-row {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    min-height: 2.8rem;
    margin-bottom: 0.6rem;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    max-width: 26rem;
    height: 2.8rem;
    padding: 0 0.4rem 0 1.1rem;
    box-sizing: border-box;
    border: none;
    border-radius: $br-pill;
    background: $c-accent-soft;
    font-size: 1.2rem;
    color: $c-on-accent-soft;

    &--empty {
      padding: 0 1.1rem;
      border: 1px dashed rgba($c-ink, 0.22);
      background: none;
      color: $c-ink-soft;
      cursor: pointer;
      transition: border-color $transition-fast ease;

      &:hover {
        border-color: rgba($c-ink, 0.5);
      }
    }
  }

  &__link-icon {
    display: flex;
    flex: none;

    :deep(svg) {
      width: 1rem;
      height: 1rem;
      fill: currentColor;
    }
  }

  &__link-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__link-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: none;
    cursor: pointer;

    &:hover {
      background: rgba($c-on-accent-soft, 0.15);
    }

    :deep(svg) {
      width: 0.9rem;
      height: 0.9rem;
      fill: currentColor;
    }
  }

  &__link-note {
    font-size: 1.1rem;
    color: rgba($c-ink, 0.5);
  }

  // ── Input ────────────────────────────────────────────────────────────────
  &__input-wrap {
    position: relative;
    padding-bottom: 0.6rem;
    border: 1px solid $c-hairline-strong;
    border-radius: $br-very-heavy;
    background: $c-surface;
    transition: border-color $transition-fast ease;

    &--filled {
      border-color: rgba($c-ink, 0.28);
    }
  }

  // textarea-custom paints its own grey well and darkens it on hover/focus. In the
  // composer the white box around it is the surface, so the control stays transparent.
  &__input-wrap &__textarea {
    background: none;

    &:hover,
    &.custom-textarea--active {
      background: none;
    }

    :deep(textarea) {
      padding: 1.4rem 1.6rem 0.6rem;
      border: none;
      background: none;
      font-size: 1.4rem;
      line-height: 2.1rem;
      resize: none;

      &:focus {
        border: none;
        box-shadow: none;
      }
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0 1rem 0 1.2rem;
  }

  &__icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    padding: 0;
    border: none;
    border-radius: $br-medium-heavy;
    background: none;
    cursor: pointer;
    transition: background $transition-fast ease;

    &:hover {
      background: $c-surface-muted;
    }
  }

  &__btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(svg) {
      width: 1.4rem;
      height: 1.4rem;
      fill: currentColor;
    }

    &--at {
      font-size: 1.4rem;
      font-weight: 700;
      color: $c-ink-soft;
    }
  }

  &__deadline-chip,
  &__recipients {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    height: 2.8rem;
    padding: 0 0.4rem 0 1rem;
    border-radius: $br-pill;
    font-size: 1.2rem;
    white-space: nowrap;
  }

  &__deadline-chip {
    background: $c-accent-soft;
    color: $c-on-accent-soft;
    font-weight: 700;
  }

  &__recipients {
    min-width: 0;
    padding-left: 0.4rem;
    background: $c-surface-muted;
    color: $c-ink-soft;
    overflow: hidden;
  }

  // The name is the only part of the chip that may give way. It was a bare text
  // node, so the chip's own `overflow: hidden` had nothing to trim but the end of
  // the row — which is where the clear button is, so a long name pushed the ×
  // out of sight rather than shortening itself.
  &__recipient-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__recipient-avatar {
    flex: none;

    :deep(.user-avatar),
    &:deep(.user-avatar__fallback) {
      width: 2.2rem;
      min-width: 2.2rem;
      height: 2.2rem;
      font-size: 1rem;
    }
  }

  &__chip-remove {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: none;
    cursor: pointer;

    &:hover {
      background: rgba($c-ink, 0.1);
    }

    :deep(svg) {
      width: 0.9rem;
      height: 0.9rem;
      fill: currentColor;
    }
  }

  &__actions-right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  // ── @mention dropdown ────────────────────────────────────────────────────
  &__mentions {
    width: 26rem;
    padding: 0.6rem 0;
    border-radius: $br-very-heavy;
    background: $c-surface;
    box-shadow: $shadow-lifted;
  }

  &__mentions-head {
    padding: 0.4rem 1.4rem 0.6rem;
    font-family: 'Montserrat', sans-serif;
    font-size: 0.9rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: rgba($c-ink, 0.45);
  }

  &__mentions-list {
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 26rem;
    overflow-y: auto;
  }

  &__mention {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 0.7rem 1.4rem;
    cursor: pointer;

    &--highlighted {
      background: $c-surface-muted;
    }

    :deep(.user-avatar),
    :deep(.user-avatar__fallback) {
      width: 2.4rem;
      min-width: 2.4rem;
      height: 2.4rem;
      font-size: 1.1rem;
    }
  }

  &__mention-name {
    font-size: 1.3rem;
    color: $c-ink;
  }

  &__mention-meta {
    font-size: 1.1rem;
    color: rgba($c-ink, 0.5);
  }

  &__role-chip {
    padding: 0.2rem 0.8rem;
    border-radius: $br-pill;
    background: rgba($c-blue, 0.1);
    color: $c-on-info-soft;
    font-size: 1.2rem;
    font-weight: 700;
  }

  &__deadline-popper {
    border-radius: $br-very-heavy;
    background: $c-surface;
    box-shadow: $shadow-lifted;
    overflow: hidden;
  }
}
</style>
